// Alt en status gjør, på statusens egen side: hvilke e-poster som går ut når en
// ordre får statusen, til hvem og når — og hvilke tidsstyrte regler som gjelder
// mens ordren står i den.
//
// E-postene er de samme radene som under «Automasjonsregler» (hendelsen
// «status endret» med denne statusen som betingelse). Der er de sortert på
// regel; her på status, som er slik admin tenker på dem.
import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
} from "@mui/material";
import Card from "@mui/material/Card";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BoltIcon from "@mui/icons-material/Bolt";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import { STATUSES as STATUS_FACTS } from "components/StatusInfoDialog";
import {
  getStatusAutomations,
  saveStatusEmail,
  toggleAutomationRule,
  deleteAutomationRule,
} from "api/apiService";
import { notifyCenter } from "utils/toast";

const LINE = "#e6ecf3";
const ROLE_LABEL = { customer: "Kunde", vendor: "Produsent", admin: "Admin" };

const recipientsText = (tpl) =>
  (tpl?.send_to || []).map((r) => ROLE_LABEL[r] || r).join(", ") || "ingen mottaker valgt i malen";

function timingText(timing) {
  if (!timing || timing.mode === "instant") return "Med en gang";
  if (timing.mode === "delay") return `${timing.minutes} minutter etter`;
  if (Number(timing.days) === 0) return `Samme dag kl. ${timing.time}`;
  if (Number(timing.days) === 1) return `Neste dag kl. ${timing.time}`;
  return `${timing.days} dager etter kl. ${timing.time}`;
}

const EMPTY_FORM = { ruleId: null, templateId: "", mode: "instant", days: 1, time: "09:00" };

function StatusAutomations({ status, selectSx }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyRule, setBusyRule] = useState(null);
  const [form, setForm] = useState(null); // null = lukket
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!status) return;
    setLoading(true);
    try {
      const res = await getStatusAutomations(status);
      if (res?.ok) setData(res);
      else notifyCenter("error", res?.message || "Kunne ikke hente e-postene for statusen.");
    } catch (e) {
      notifyCenter("error", "Kunne ikke hente e-postene for statusen.");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  if (!status) return null;

  const label = data?.label || status;
  const emails = data?.emails || [];
  const scheduled = data?.scheduled || [];
  const templates = data?.templates || [];
  const activeCount = emails.filter((e) => e.enabled).length;
  const facts = STATUS_FACTS.find((s) => s.key === status);

  const toggle = async (ruleId, enabled) => {
    setBusyRule(ruleId);
    try {
      const res = await toggleAutomationRule(ruleId, enabled);
      if (res?.ok) await load();
      else notifyCenter("error", res?.message || "Kunne ikke endre regelen.");
    } catch (e) {
      notifyCenter("error", "Kunne ikke endre regelen.");
    } finally {
      setBusyRule(null);
    }
  };

  const remove = async (email) => {
    setBusyRule(email.rule_id);
    try {
      const res = await deleteAutomationRule(email.rule_id);
      if (res?.ok) {
        notifyCenter("success", "E-posten er fjernet fra statusen.");
        await load();
      } else {
        notifyCenter("error", res?.message || "Kunne ikke fjerne e-posten.");
      }
    } catch (e) {
      notifyCenter("error", "Kunne ikke fjerne e-posten.");
    } finally {
      setBusyRule(null);
    }
  };

  const openEdit = (email) =>
    setForm({
      ruleId: email.rule_id,
      templateId: email.template?.id || "",
      mode: email.timing?.mode === "later" ? "later" : "instant",
      days: email.timing?.mode === "later" ? email.timing.days : 1,
      time: email.timing?.mode === "later" ? email.timing.time : "09:00",
      sharedWith: email.shared_with || [],
    });

  const save = async () => {
    if (!form.templateId) {
      notifyCenter("warning", "Velg en e-postmal.");
      return;
    }
    setSaving(true);
    try {
      const res = await saveStatusEmail({
        status,
        ruleId: form.ruleId,
        templateId: form.templateId,
        timing:
          form.mode === "later"
            ? { mode: "later", days: Number(form.days), time: form.time }
            : { mode: "instant" },
      });
      if (res?.ok) {
        notifyCenter("success", res.message || "Lagret.");
        setForm(null);
        await load();
      } else {
        notifyCenter("error", res?.message || "Kunne ikke lagre e-posten.");
      }
    } catch (e) {
      notifyCenter("error", "Kunne ikke lagre e-posten.");
    } finally {
      setSaving(false);
    }
  };

  const chosenTemplate = templates.find((t) => t.id === form?.templateId);

  return (
    <Card sx={{ mt: 3 }}>
      <SoftBox p={3} display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap">
        <SoftBox>
          <SoftTypography variant="h5" fontWeight="bold">
            Automatiske e-poster
          </SoftTypography>
          <SoftTypography variant="button" color="text" sx={{ textTransform: "none" }}>
            Dette sendes når en ordre får statusen «{label}».
          </SoftTypography>
        </SoftBox>
        {data && (
          <Chip
            icon={<MailOutlineIcon />}
            color={activeCount ? "success" : "default"}
            label={
              activeCount
                ? `Sender ${activeCount} e-post${activeCount === 1 ? "" : "er"} automatisk`
                : "Sender ingen e-post automatisk"
            }
          />
        )}
      </SoftBox>
      <Divider sx={{ my: 0 }} />

      <SoftBox p={3}>
        {loading && !data && (
          <SoftBox display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} />
            <SoftTypography variant="caption" color="text">
              Henter …
            </SoftTypography>
          </SoftBox>
        )}

        {data && !emails.length && (
          <SoftTypography variant="button" color="text" display="block" sx={{ textTransform: "none" }}>
            Ingen e-post er koblet til denne statusen ennå.
          </SoftTypography>
        )}

        {emails.map((email) => (
          <SoftBox
            key={email.rule_id}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              border: `1px solid ${LINE}`,
              borderRadius: 2,
              p: 2,
              mb: 1.5,
              opacity: email.enabled ? 1 : 0.65,
            }}
          >
            <Switch
              checked={email.enabled}
              disabled={busyRule === email.rule_id}
              onChange={(e) => toggle(email.rule_id, e.target.checked)}
              inputProps={{ "aria-label": `Send «${email.template?.template_name || email.name}» automatisk` }}
            />
            <SoftBox sx={{ flex: 1, minWidth: 0 }}>
              <SoftTypography variant="button" fontWeight="bold" display="block" sx={{ textTransform: "none" }}>
                {email.template?.template_name || email.name}
              </SoftTypography>
              {email.template_missing ? (
                <SoftBox display="flex" alignItems="center" gap={0.5} mt={0.5}>
                  <WarningAmberIcon sx={{ fontSize: 16, color: "#c2410c" }} />
                  <SoftTypography variant="caption" sx={{ color: "#c2410c" }}>
                    Malen finnes ikke lenger. Velg en ny med «Endre», ellers sendes ingenting.
                  </SoftTypography>
                </SoftBox>
              ) : (
                <SoftTypography variant="caption" color="text" display="block" sx={{ fontSize: 13 }}>
                  Emne: {email.template.subject}
                </SoftTypography>
              )}
              <SoftBox display="flex" flexWrap="wrap" gap={0.75} mt={1}>
                <Chip
                  size="small"
                  icon={email.timing?.mode === "instant" ? <BoltIcon /> : <ScheduleIcon />}
                  color={email.timing?.mode === "instant" ? "info" : "warning"}
                  label={timingText(email.timing)}
                />
                {!email.template_missing && (
                  <Chip size="small" variant="outlined" label={`Til: ${recipientsText(email.template)}`} />
                )}
                {!email.enabled && <Chip size="small" variant="outlined" label="Slått av" />}
              </SoftBox>
              {!!email.shared_with?.length && (
                <SoftTypography variant="caption" color="text" display="block" mt={0.75}>
                  Samme regel gjelder også: {email.shared_with.join(", ")}.
                </SoftTypography>
              )}
            </SoftBox>
            <SoftBox display="flex" gap={0.5} flexShrink={0}>
              <SoftButton size="small" variant="text" color="info" onClick={() => openEdit(email)}>
                Endre
              </SoftButton>
              {/* Systemregler kan slås av, men ikke slettes (samme som under
                  Automasjonsregler). */}
              {!email.is_system && (
                <SoftButton
                  size="small"
                  variant="text"
                  color="error"
                  disabled={busyRule === email.rule_id}
                  onClick={() => remove(email)}
                >
                  Fjern
                </SoftButton>
              )}
            </SoftBox>
          </SoftBox>
        ))}

        {data && (
          <SoftButton size="small" variant="gradient" color="info" onClick={() => setForm({ ...EMPTY_FORM })}>
            <AddIcon sx={{ mr: 0.5 }} /> Legg til e-post
          </SoftButton>
        )}

        {/* Tidsstyrte regler */}
        {!!scheduled.length && (
          <SoftBox mt={4}>
            <SoftTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
              Tidsstyrt mens ordren står i statusen
            </SoftTypography>
            <SoftTypography variant="caption" color="text" display="block" mb={1.5}>
              Disse følger ordrens alder eller leveringstid, ikke tidspunktet statusen ble satt. De endres
              under «Automasjonsregler».
            </SoftTypography>
            {scheduled.map((rule) => (
              <SoftBox
                key={rule.rule_id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  border: `1px solid ${LINE}`,
                  borderRadius: 2,
                  p: 1.5,
                  mb: 1,
                  opacity: rule.enabled ? 1 : 0.65,
                }}
              >
                <Switch
                  checked={rule.enabled}
                  disabled={busyRule === rule.rule_id}
                  onChange={(e) => toggle(rule.rule_id, e.target.checked)}
                  inputProps={{ "aria-label": rule.name }}
                />
                <SoftBox sx={{ flex: 1, minWidth: 0 }}>
                  <SoftTypography variant="button" fontWeight="bold" display="block" sx={{ textTransform: "none" }}>
                    {rule.action_type === "set_status"
                      ? `Setter status til «${rule.set_status_label}»`
                      : rule.template?.template_name || rule.name}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" sx={{ fontSize: 13 }}>
                    {rule.when_text}
                    {rule.template ? ` · til: ${recipientsText(rule.template)}` : ""}
                  </SoftTypography>
                </SoftBox>
                <SoftButton
                  size="small"
                  variant="text"
                  color="secondary"
                  component="a"
                  href={`/automation-rules/edit/${rule.rule_id}`}
                >
                  Åpne regel
                </SoftButton>
              </SoftBox>
            ))}
          </SoftBox>
        )}

        {/* Det statusen ellers gjør */}
        {facts && (
          <SoftBox mt={4}>
            <SoftTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
              Ellers
            </SoftTypography>
            <SoftTypography variant="caption" color="text" display="block" sx={{ fontSize: 13 }}>
              {facts.text}
            </SoftTypography>
            <SoftTypography variant="caption" color="text" display="block" mt={0.5} sx={{ fontSize: 13 }}>
              <strong>Produsentens lenke:</strong> {facts.link}
            </SoftTypography>
          </SoftBox>
        )}

        <SoftTypography variant="caption" color="text" display="block" mt={3}>
          Endrer du status fra ordren, får du se e-posten som går med en gang og kan rette den før den
          sendes. E-poster som er satt til senere, går av seg selv. «Endre status uten e-post» sender ingen
          av dem. Alt stanses av bryteren «Kundemeldinger» under E-postinnstillinger.
        </SoftTypography>
      </SoftBox>

      {/* Legg til / endre */}
      <Dialog open={!!form} onClose={() => !saving && setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>
          {form?.ruleId ? "Endre e-post" : "Legg til e-post"} for «{label}»
        </DialogTitle>
        {form && (
          <DialogContent>
            <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
              <SoftTypography variant="caption" fontWeight="bold">
                E-postmal
              </SoftTypography>
              <SoftBox display="flex" gap={0.5}>
                <SoftButton
                  size="small"
                  variant="text"
                  color="info"
                  component="a"
                  href="/email-settings/create-template"
                  target="_blank"
                  rel="noreferrer"
                >
                  <OpenInNewIcon sx={{ fontSize: 15, mr: 0.5 }} /> Lag ny mal
                </SoftButton>
                <SoftButton size="small" variant="text" color="secondary" onClick={load} disabled={loading}>
                  <RefreshIcon sx={{ fontSize: 15, mr: 0.5 }} /> Oppdater listen
                </SoftButton>
              </SoftBox>
            </SoftBox>
            <FormControl fullWidth>
              <Select
                value={form.templateId}
                displayEmpty
                sx={selectSx}
                onChange={(e) => setForm((f) => ({ ...f, templateId: e.target.value }))}
              >
                <MenuItem value="">Velg mal …</MenuItem>
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.template_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {chosenTemplate && (
              <SoftTypography variant="caption" color="text" display="block" mt={0.75}>
                Emne: {chosenTemplate.subject} · Sendes til: {recipientsText(chosenTemplate)}
                {chosenTemplate.prevent_duplicates
                  ? " · Sendes bare én gang per ordre (innstilling i malen)."
                  : ""}
              </SoftTypography>
            )}
            <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
              En ny mal åpnes i en egen fane. Velg e-posttype «Custom», lagre, og trykk «Oppdater listen» her.
            </SoftTypography>

            <SoftTypography variant="caption" fontWeight="bold" display="block" mt={2.5}>
              Når skal den sendes?
            </SoftTypography>
            <RadioGroup
              value={form.mode}
              onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))}
              sx={{ ml: 1 }}
            >
              <FormControlLabel
                value="instant"
                control={<Radio />}
                label={
                  <SoftTypography variant="button" fontWeight="regular" sx={{ textTransform: "none" }}>
                    Med en gang statusen settes
                  </SoftTypography>
                }
              />
              <FormControlLabel
                value="later"
                control={<Radio />}
                label={
                  <SoftTypography variant="button" fontWeight="regular" sx={{ textTransform: "none" }}>
                    Senere, på et fast klokkeslett
                  </SoftTypography>
                }
              />
            </RadioGroup>

            {form.mode === "later" && (
              <SoftBox mt={1} ml={1}>
                <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <SoftBox sx={{ width: 90 }}>
                    <SoftInput
                      type="number"
                      inputProps={{ min: 1, max: 365, "aria-label": "Antall dager etter" }}
                      value={form.days}
                      onChange={(e) => setForm((f) => ({ ...f, days: e.target.value }))}
                    />
                  </SoftBox>
                  <SoftTypography variant="button" fontWeight="regular" sx={{ textTransform: "none" }}>
                    {Number(form.days) === 1 ? "dag etter, kl." : "dager etter, kl."}
                  </SoftTypography>
                  <SoftBox sx={{ width: 130 }}>
                    <SoftInput
                      type="time"
                      inputProps={{ "aria-label": "Klokkeslett" }}
                      value={form.time}
                      onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                    />
                  </SoftBox>
                </SoftBox>
                <SoftTypography variant="caption" color="text" display="block" mt={1}>
                  {timingText({ mode: "later", days: form.days, time: form.time })}, norsk tid. Sendes bare
                  hvis ordren fortsatt står i «{label}» da — passer til påminnelser.
                </SoftTypography>
              </SoftBox>
            )}

            {!!form.sharedWith?.length && (
              <SoftTypography variant="caption" display="block" mt={2} sx={{ color: "#c2410c" }}>
                Denne regelen gjelder også {form.sharedWith.join(", ")}. Endringen gjelder der også.
              </SoftTypography>
            )}

            <SoftBox mt={3} display="flex" gap={1} justifyContent="flex-end">
              <SoftButton size="small" variant="text" color="secondary" disabled={saving} onClick={() => setForm(null)}>
                Avbryt
              </SoftButton>
              <SoftButton size="small" variant="gradient" color="info" disabled={saving} onClick={save}>
                {saving ? "Lagrer …" : form.ruleId ? "Lagre" : "Legg til"}
              </SoftButton>
            </SoftBox>
          </DialogContent>
        )}
      </Dialog>
    </Card>
  );
}

StatusAutomations.propTypes = {
  status: PropTypes.string,
  selectSx: PropTypes.object,
};

StatusAutomations.defaultProps = { status: "", selectSx: {} };

export default StatusAutomations;
